import { useCallback } from "react";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import useUserPreferences from "components/hooks/useUserPreferences";
import { ColumnSettingsProps, Sizes, SortList } from "@cofano/cck";
import { FilterSettingsProviderProps as UserFilterSettings } from "@cofano/cck/Filter/FilterSettingsProvider";

export type UserTableSettings = {
	columnSettings: Array<ColumnSettingsProps>,
	size: keyof typeof Sizes,
	sort?: SortList,
	pageSize: number,
}

export type BOSSettingsProvider = {
	filter: {
		settings: (id: string) => UserFilterSettings,
		onSave: (id: string, settings: UserFilterSettings) => void,
		onReset: (id: string) => void
	},
	table: {
		settings: (id: string) => UserTableSettings
		onSave: (id: string, columnSettings: Array<ColumnSettingsProps>, size: keyof typeof Sizes, pageSize: number, sort?: SortList) => void,
		onReset: (id: string) => void }
}

export const settingsProvider = () : BOSSettingsProvider => {
	const user = useStoreState(store => store.session.user);
	const saveUserPreferences = useUserPreferences();

	const handleSaveSettings = useCallback((id: string, body: UserFilterSettings | UserTableSettings) => {
		saveUserPreferences({
			preferences: {
				[id]: body,
			},
		});
	}, []);

	const handleResetSettings = useCallback((id: string) => {
		const currentPreferences = (user?.properties.userPreferences || {}) as Record<string, unknown>;
		delete currentPreferences[id];

		saveUserPreferences({
			preferences: currentPreferences,
		});

	}, []);

	return {
		filter: {
			settings: (id: string) => user?.properties.userPreferences?.[id] as UserFilterSettings,
			onSave: (id: string, settings: UserFilterSettings) => handleSaveSettings(id, settings),
			onReset: (id: string) => handleResetSettings(id),
		},
		table: {
			settings: (id: string) => user?.properties.userPreferences?.[id] as UserTableSettings,
			onSave: (id: string, columnSettings: Array<ColumnSettingsProps>, size: keyof typeof Sizes, pageSize: number, sort?: SortList) => {
				handleSaveSettings(id, {
					columnSettings: columnSettings,
					size: size,
					pageSize: pageSize,
					sort: sort,
				});
			},
			onReset: (id: string) => handleResetSettings(id),
		},
	};
};

export const tableLocalization = {
	selectionToolbar: {
		selectionLabel: intl.formatMessage({ id: "TABLE_SELECTION_TOOLBAR_SELECTION" }),
		selectAllLabel: intl.formatMessage({ id: "TABLE_SELECTION_TOOLBAR_SELECT_ALL" }),
		clearSelectionLabel: intl.formatMessage({ id: "TABLE_SELECTION_TOOLBAR_CLEAR_SELECTION" }),
	},
	body: {
		emptyContentLabel: intl.formatMessage({ id: "TABLE_BODY_EMPTY" }),
	},
	columnProvider: {
		columnsLabel: intl.formatMessage({ id: "TABLE_COLUMN_PROVIDER_COLUMNS" }),
		saveLabel: intl.formatMessage({ id: "TABLE_COLUMN_PROVIDER_SAVE" }),
		saveTooltipLabel: intl.formatMessage({ id: "TABLE_COLUMN_PROVIDER_SAVE_TOOLTIP" }),
		resetLabel: intl.formatMessage({ id: "TABLE_COLUMN_PROVIDER_RESET" }),
		filterGroupsEnabled: {
			infoLabel: intl.formatMessage({ id: "TABLE_SETTINGS_PROVIDER_FILTER_INFO_LABEL" }),
			saveTooltipLabel: intl.formatMessage({ id: "TABLE_SETTINGS_PROVIDER_FILTER_SAVE_TOOLTIP_LABEL" }),
		},
	},
	pagination: {
		pageLabel: intl.formatMessage({ id: "TABLE_PAGINATION_PAGE" }),
		ofLabel: intl.formatMessage({ id: "TABLE_PAGINATION_OF" }),
	},
	filter: {
		yesLabel: intl.formatMessage({ id: "YES" }),
		noLabel: intl.formatMessage({ id: "NO" }),
	},
};

export const filterLocalization = {
	toolbar: {
		filtersLabel: intl.formatMessage({ id: "FILTER_TOOLBAR_FILTERS" }),
		showGroupPresetsLabel: "Show group presets",
	},
	groups: {
		newGroupLabel: intl.formatMessage({ id: "FILTER_GROUPS_NEW_GROUP" }),
		groupNameLabel: intl.formatMessage({ id: "FILTER_GROUPS_GROUP_NAME" }),
		nameLabel: intl.formatMessage({ id: "FILTER_GROUPS_NAME" }),
		editLabel: intl.formatMessage({ id: "FILTER_GROUPS_EDIT" }),
		addLabel: intl.formatMessage({ id: "FILTER_GROUPS_ADD" }),
		cancelLabel: intl.formatMessage({ id: "FILTER_GROUPS_CANCEL" }),
		confirmLabel: intl.formatMessage({ id: "CONFIRM" }),
		confirmDeleteLabel: intl.formatMessage({ id: "CONFIRM_DELETE_DATA" }),
	},
	filter: {
		yesLabel: intl.formatMessage({ id: "YES" }),
		noLabel: intl.formatMessage({ id: "NO" }),
	},
	footer: {
		searchLabel: intl.formatMessage({ id: "FILTER_FOOTER_SEARCH" }),
		clearLabel: intl.formatMessage({ id: "FILTER_FOOTER_CLEAR" }),
		editGroupNameTooltipLabel: intl.formatMessage({ id: "FILTER_FOOTER_EDIT_GROUP_NAME_TOOLTIP" }),
		deleteGroupTooltipLabel: intl.formatMessage({ id: "FILTER_FOOTER_DELETE_GROUP_TOOLTIP" }),
		duplicateGroupTooltipLabel: intl.formatMessage({ id: "FILTER_FOOTER_DUPLICATE_GROUP_TOOLTIP" }),
		saveLabel: intl.formatMessage({ id: "FILTER_FOOTER_SAVE" }),
		saveTooltipLabel: intl.formatMessage({ id: "FILTER_FOOTER_SAVE_TOOLTIP" }),
		saveFilterGroupTooltipLabel: intl.formatMessage({ id: "FILTER_FOOTER_SAVE_FILTER_GROUP_TOOLTIP" }),
		cantSaveDefaultGroup: intl.formatMessage({ id: "FILTER_FOOTER_DEFAULT_SAVE" }),
	},
	fieldProvider: {
		fieldsLabel: intl.formatMessage({ id: "FILTER_FIELD_PROVIDER_FIELDS" }),
		resetLabel: intl.formatMessage({ id: "FILTER_FIELD_PROVIDER_RESET" }),
	},
};
